import { InputAdornment, Stack, Typography } from '@mui/material';
import { isEmpty } from 'lodash';
import { useEffect, useMemo, useState } from 'react';

import { SubpageLayout } from '@/components/architecture/Layout/SubpageLayout';
import { SelectInput } from '@/components/form/baseInputs/SelectInput';
import { Briefcase02Icon } from '@/components/icons/Briefcase02Icon';
import { Card } from '@/components/layout/Card/Card';
import { UnsavedChangesConfirmationModal } from '@/components/modals/ConfirmationModal/UnsavedChangesConfirmationModal';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { FormProvider, useForm } from '@/components/react-hook-form';
import { useSubBrandOptions } from '@/modules/admin/branding/hooks/useSubBrandOptions';
import { getNodes } from '@/utils/graphqlUtils';

import { AdminBrandingForm } from './AdminBrandingForm';
import { defaultFormValues } from './AdminBrandingForm.constants';
import { FirmBrandingFormShape } from './AdminBrandingForm.types';
import {
  useTenantBrandingQuery,
  useUpdateBrandingMutation,
  useUpdateSubBrandBrandingMutation,
} from './graphql/AdminBrandingPage.generated';

export function AdminBrandingPage() {
  const [selectedActiveBrand, setSelectedActiveBrand] = useState<string>('');
  const { createErrorFeedback, createSuccessFeedback } = useFeedback();
  const { data } = useTenantBrandingQuery({
    onError: createErrorFeedback('Failed to load branding information.'),
  });
  const onError = createErrorFeedback(
    'Failed to save branding update. Please refresh the page and try again.'
  );
  const onCompleted = createSuccessFeedback(
    'Successfully updated branding information.'
  );
  const [updateTenantBranding] = useUpdateBrandingMutation({
    onError,
    onCompleted,
  });

  const [updateSubBrandBranding] = useUpdateSubBrandBrandingMutation({
    onError,
    onCompleted,
  });

  const formMethods = useForm<FirmBrandingFormShape>({
    defaultValues: defaultFormValues,
  });

  const { reset, handleSubmit } = formMethods;

  // Aggregate dirty signal from every form on this page. Sourced
  // directly from each form's `isDirty` (via AdminBrandingForm) so the
  // prompt fires for any user edit and stops firing after a successful
  // save (post-save refetch triggers reset → `isDirty` flips false).
  const [isAnyFormDirty, setIsAnyFormDirty] = useState(false);
  // Hard-block sub-brand switching while any form is mid-save.
  const [isAnyFormSubmitting, setIsAnyFormSubmitting] = useState(false);

  // `null` = no pending change. Use a separate boolean to distinguish
  // from a pending switch back to the tenant default ('').
  const [pendingSubBrand, setPendingSubBrand] = useState<string | null>(null);

  const handleSubBrandSelect = (value: string) => {
    if (value === selectedActiveBrand) return;
    if (isAnyFormSubmitting) return;
    if (isAnyFormDirty) {
      setPendingSubBrand(value);
      return;
    }
    setSelectedActiveBrand(value);
  };

  const handleSubBrandConfirmModalClose = (reason: 'confirm' | 'cancel') => {
    if (reason === 'cancel') {
      // "Leave" — apply the pending sub-brand change. The existing
      // reset effects in this page and SlideConfigForm will reload
      // the new sub-brand's data into the forms.
      if (pendingSubBrand !== null) {
        setSelectedActiveBrand(pendingSubBrand);
      }
    }
    setPendingSubBrand(null);
  };

  const subBrands = useMemo(() => getNodes(data?.subBrands), [data?.subBrands]);
  const tenantBranding = useMemo(() => {
    if (!data) return null;
    if (!selectedActiveBrand) {
      return data.tenantInformation.branding ?? null;
    }

    const brand = subBrands.find(
      (subBrand) => subBrand.id === selectedActiveBrand
    );
    return brand ?? data.tenantInformation.branding ?? null;
  }, [data, selectedActiveBrand, subBrands]);

  useEffect(() => {
    if (!tenantBranding) return;

    reset({
      primaryColor:
        tenantBranding.primaryColor || defaultFormValues.primaryColor,
      secondaryColor:
        tenantBranding.secondaryColor || defaultFormValues.secondaryColor,
      dataVisualizationPrimaryColor:
        tenantBranding.dataVisualizationPrimaryColor ||
        defaultFormValues.dataVisualizationPrimaryColor,
      dataVisualizationSecondaryColor:
        tenantBranding.dataVisualizationSecondaryColor ||
        defaultFormValues.dataVisualizationSecondaryColor,

      dataVisualizationTertiaryColor:
        tenantBranding.dataVisualizationTertiaryColor ||
        defaultFormValues.dataVisualizationTertiaryColor,
      dataVisualizationNegativeColor:
        tenantBranding.dataVisualizationNegativeColor ||
        defaultFormValues.dataVisualizationNegativeColor,
      buttonsLinkColor:
        tenantBranding.buttonsLinkColor || defaultFormValues.buttonsLinkColor,
      presentationHeaderFontFace:
        tenantBranding.presentationHeaderFontFace ||
        defaultFormValues.presentationHeaderFontFace,
    });
  }, [data, reset, selectedActiveBrand, subBrands, tenantBranding]);

  const subBrandOptions = useSubBrandOptions(subBrands);

  const onSubmit = async (formData: FirmBrandingFormShape) => {
    if (!data) {
      throw new Error('Invalid submission state');
    }

    if (selectedActiveBrand) {
      return updateSubBrandBranding({
        variables: {
          input: {
            id: selectedActiveBrand,
            update: formData,
          },
        },
      });
    }

    const tenantBrandingId = data.tenantInformation.branding.id;

    return updateTenantBranding({
      variables: {
        id: tenantBrandingId,
        input: formData,
      },
    });
  };

  return (
    <SubpageLayout heading="Branding">
      <FormProvider {...formMethods}>
        <Card variant="outlined" sx={{ p: 2 }}>
          {!isEmpty(subBrands) && (
            <Stack spacing={2} pb={2}>
              <Stack>
                <Typography variant="title-xl">Sub-brand</Typography>
                <Typography variant="body-s">
                  Select the sub-brand you would like to specify branding for
                </Typography>
              </Stack>
              <SelectInput
                disabled={isAnyFormSubmitting}
                options={subBrandOptions}
                showEmptyValue={false}
                startAdornment={
                  <InputAdornment
                    sx={{ backgroundColor: 'white' }}
                    position="start"
                  >
                    <Briefcase02Icon size={16} />
                  </InputAdornment>
                }
                value={selectedActiveBrand}
                onChange={(e) => handleSubBrandSelect(e.target.value)}
                label="Active brand"
                hideLabel
              />
            </Stack>
          )}
          <AdminBrandingForm
            onSubmit={handleSubmit(onSubmit)}
            tenantBranding={tenantBranding}
            selectedSubBrandId={selectedActiveBrand || null}
            onAnyDirtyChange={setIsAnyFormDirty}
            onAnySubmittingChange={setIsAnyFormSubmitting}
          />
        </Card>
      </FormProvider>
      {pendingSubBrand !== null && (
        <UnsavedChangesConfirmationModal
          isOpen={true}
          onClose={handleSubBrandConfirmModalClose}
        />
      )}
    </SubpageLayout>
  );
}
